import { useState, useEffect } from "react";
import { clearSavedFilters } from "@/lib/filterUtils";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { Calendar as CalendarIcon, X, Download, Filter } from "lucide-react";
import { toast } from "sonner";

interface IssueFiltersProps {
  onFilterChange: (filters: {
    category: string;
    status: string;
    startDate?: Date;
    endDate?: Date;
    severityScore?: number;
    userType?: string;
    ward?: string;
  }) => void;
  onExport?: (filters: {
    category: string;
    status: string;
    startDate?: Date;
    endDate?: Date;
    severityScore?: number;
    userType?: string;
    ward?: string;
  }) => void;
  initialFilters?: {
    category: string;
    status: string;
    startDate?: Date;
    endDate?: Date;
    severityScore?: number;
    userType?: string;
    ward?: string;
  };
  hideSeverityScore?: boolean; // New prop to hide severity score field
}

const IssueFilters = ({ onFilterChange, onExport, initialFilters, hideSeverityScore = false }: IssueFiltersProps) => {
  const [category, setCategory] = useState<string>(initialFilters?.category || "all");
  const [status, setStatus] = useState<string>(initialFilters?.status || "all");
  const [startDate, setStartDate] = useState<Date | undefined>(initialFilters?.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(initialFilters?.endDate);
  const [severityScore, setSeverityScore] = useState<string>(initialFilters?.severityScore?.toString() || "");
  const [userType, setUserType] = useState<string>(initialFilters?.userType || "all");
  const [ward, setWard] = useState<string>(initialFilters?.ward || "all");
  const [isExporting, setIsExporting] = useState(false);

  // Sync local state when initialFilters change
  useEffect(() => {
    if (initialFilters) {
      setCategory(initialFilters.category || "all");
      setStatus(initialFilters.status || "all");
      setStartDate(initialFilters.startDate);
      setEndDate(initialFilters.endDate);
      setSeverityScore(initialFilters.severityScore?.toString() || "");
      setUserType(initialFilters.userType || "all");
      setWard(initialFilters.ward || "all");
    }
  }, [initialFilters]);
  
  const handleCategoryChange = (value: string) => {
    setCategory(value);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(value, status, startDate, endDate, isNaN(numValue!) ? undefined : numValue, userType, ward);
  };
  
  const handleStatusChange = (value: string) => {
    setStatus(value);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(category, value, startDate, endDate, isNaN(numValue!) ? undefined : numValue, userType, ward);
  };
  
  const handleStartDateChange = (selectedDate: Date | undefined) => {
    setStartDate(selectedDate);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(category, status, selectedDate, endDate, isNaN(numValue!) ? undefined : numValue, userType, ward);
  };

  const handleEndDateChange = (selectedDate: Date | undefined) => {
    setEndDate(selectedDate);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(category, status, startDate, selectedDate, isNaN(numValue!) ? undefined : numValue, userType, ward);
  };

  const handleSeverityScoreChange = (value: string) => {
    setSeverityScore(value);
    const numValue = value === "" ? undefined : parseInt(value);
    updateFilters(category, status, startDate, endDate, numValue, userType, ward);
  };

  const handleUserTypeChange = (value: string) => {
    setUserType(value);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(category, status, startDate, endDate, isNaN(numValue!) ? undefined : numValue, value, ward);
  };

  const handleWardChange = (value: string) => {
    setWard(value);
    const numValue = severityScore === "" ? undefined : parseInt(severityScore);
    updateFilters(category, status, startDate, endDate, isNaN(numValue!) ? undefined : numValue, userType, value);
  };

  const updateFilters = (
    cat: string,
    stat: string,
    start: Date | undefined,
    end: Date | undefined,
    severity: number | undefined,
    userTypeFilter: string,
    wardFilter: string
  ) => {
    onFilterChange({
      category: cat,
      status: stat,
      startDate: start,
      endDate: end,
      severityScore: severity,
      userType: userTypeFilter,
      ward: wardFilter,
    });
  };
  
  const clearFilters = () => {
    setCategory("all");
    setStatus("all");
    setStartDate(undefined);
    setEndDate(undefined);
    setSeverityScore("");
    setUserType("all");
    setWard("all");
    
    // Clear filters from localStorage
    clearSavedFilters();
    
    onFilterChange({
      category: "all",
      status: "all",
      startDate: undefined,
      endDate: undefined,
      severityScore: undefined,
      userType: "all",
      ward: "all",
    });
  };

  const handleExport = async () => {
    if (!onExport) return;
    
    setIsExporting(true);
    try {
      const numValue = severityScore === "" ? undefined : parseInt(severityScore);
      await onExport({
        category,
        status,
        startDate,
        endDate,
        severityScore: isNaN(numValue!) ? undefined : numValue,
        userType,
        ward,
      });
    } catch {
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const hasActiveFilters = category !== "all" || 
    status !== "all" || 
    startDate !== undefined || 
    endDate !== undefined || 
    severityScore !== "" ||
    userType !== "all" ||
    ward !== "all";

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <h3 className="text-sm font-medium">Filters</h3>
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
          )}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={clearFilters}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {/* Category Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Category</Label>
          <Select value={category} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="GARBAGE">Garbage</SelectItem>
              <SelectItem value="POTHOLE">Pothole</SelectItem>
              <SelectItem value="ELECTRIC_HAZARD">Electric Hazard</SelectItem>
              <SelectItem value="STRAY_CATTLE">Stray CATTLE</SelectItem>
              <SelectItem value="CONSTRUCTION_DEBRIS">Construction Debris</SelectItem>
              <SelectItem value="STAGNANT_WATER">Stagnant water</SelectItem>
              <SelectItem value="BURNING_WASTE">Burning waste</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Status Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Status</Label>
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="NEW">New</SelectItem>
              <SelectItem value="IN_REVIEW">In Review</SelectItem>
              <SelectItem value="RESOLVED">Resolved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="SPAM">Spam</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Start Date Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={handleStartDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* End Date Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">End Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={handleEndDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Severity Score Filter */}
        {!hideSeverityScore && (
          <div className="space-y-2">
            <Label className="text-xs font-medium">Severity Score (0-100)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              placeholder="Enter score"
              value={severityScore}
              onChange={(e) => handleSeverityScoreChange(e.target.value)}
              className="w-full"
            />
          </div>
        )}

        {/* User Type Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">User Type</Label>
          <Select value={userType} onValueChange={handleUserTypeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="c1">C1 Users</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Ward Filter */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Ward</Label>
          <Select value={ward} onValueChange={handleWardChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="All Wards" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Wards</SelectItem>
              <SelectItem value="1">Ward 1 – New Palam Vihar, Rajendra Park, Sector 111 ...</SelectItem>
              <SelectItem value="2">Ward 2 – Palam Vihar, Sector 22, Sector 23 ...</SelectItem>
              <SelectItem value="3">Ward 3 – Dundahera, Sector 21, Sector 22 ...</SelectItem>
              <SelectItem value="4">Ward 4 – Udyog Vihar, Maruti Udyog, Sector 18 ...</SelectItem>
              <SelectItem value="5">Ward 5 – Ashok Vihar Phase 3, Palam Vihar Extn, Shitla Colony ...</SelectItem>
              <SelectItem value="6">Ward 6 – Ashok Vihar, Bhimgarh Kheri, Palam Vihar ...</SelectItem>
              <SelectItem value="7">Ward 7 – Sanjay Gram (Sector 13), Sector 105 ...</SelectItem>
              <SelectItem value="8">Ward 8 – Basai, Dhanwapur, Sector 102 ...</SelectItem>
              <SelectItem value="9">Ward 9 – Ambedkar Nagar, Sector 9, Surya Vihar ...</SelectItem>
              <SelectItem value="10">Ward 10 – Laxman Vihar, Sector 4, Cancon Enclave ...</SelectItem>
              <SelectItem value="11">Ward 11 – Krishna Colony, Sector 7, Jyoti Park ...</SelectItem>
              <SelectItem value="12">Ward 12 – Arjun Nagar, New Colony, Madanpuri ...</SelectItem>
              <SelectItem value="13">Ward 13 – Bhim Nagar, Ram Nagar, Subhash Nagar ...</SelectItem>
              <SelectItem value="14">Ward 14 – Gurugram Village, Sector 5, Dayanand Colony ...</SelectItem>
              <SelectItem value="15">Ward 15 – Sector 12A, Old DLF, Rajiv Nagar West ...</SelectItem>
              <SelectItem value="16">Ward 16 – Behrampur, Fazilpur Jharsa, Sector 71 ...</SelectItem>
              <SelectItem value="17">Ward 17 – Sector 14, Sector 15 Part 2, Sukhrali ...</SelectItem>
              <SelectItem value="18">Ward 18 – Civil Lines, Patel Nagar, Sector 15 Part 1 ...</SelectItem>
              <SelectItem value="19">Ward 19 – Shivaji Nagar, Shanti Nagar, Laxmi Garden ...</SelectItem>
              <SelectItem value="20">Ward 20 – Anaj Mandi, Raj Nagar, Shivji Park ...</SelectItem>
              <SelectItem value="21">Ward 21 – Gandhi Nagar, Veer Nagar, Auto Market ...</SelectItem>
              <SelectItem value="22">Ward 22 – Kadipur, Sector 10, Sector 37C ...</SelectItem>
              <SelectItem value="23">Ward 23 – Khandsa, Pace City, Sector 10A ...</SelectItem>
              <SelectItem value="24">Ward 24 – Kherki Daula, Sector 83, Narsinghpur ...</SelectItem>
              <SelectItem value="25">Ward 25 – Badshapur, Sector 66, Sector 70 ...</SelectItem>
              <SelectItem value="26">Ward 26 – Nirvana, Sector 49, South City 2 ...</SelectItem>
              <SelectItem value="27">Ward 27 – Islampur, Sector 33, Medanta (Medi City) ...</SelectItem>
              <SelectItem value="28">Ward 28 – Jharsa, Sector 31, Sector 39 ...</SelectItem>
              <SelectItem value="29">Ward 29 – Sector 29, Sector 40, South City 1 ...</SelectItem>
              <SelectItem value="30">Ward 30 – Sector 46, Sector 51, Malibu Town ...</SelectItem>
              <SelectItem value="31">Ward 31 – Ardee City, Sector 52, Wazirabad ...</SelectItem>
              <SelectItem value="32">Ward 32 – DLF Phase 5, Sector 56, Ghata ...</SelectItem>
              <SelectItem value="33">Ward 33 – Sushant Lok Phase 1, Sector 43, Chakkarpur ...</SelectItem>
              <SelectItem value="34">Ward 34 – DLF Phase 1, DLF Phase 4, Sikanderpur Ghosi ...</SelectItem>
              <SelectItem value="35">Ward 35 – DLF Phase 3, Nathupur, Ambience Island ...</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default IssueFilters;